import ky, { type KyInstance } from 'ky';

import { getAccessToken } from '../stores/authStore';

import { parseHttpError } from './errors';
import { refreshAccessToken } from './refreshToken';

import { env } from '@/lib/env';

const REFRESH_PATH = '/auth/refresh';

export const http: KyInstance = ky.create({
  prefixUrl: env.VITE_API_URL,
  credentials: 'include',
  timeout: 30_000,
  retry: { limit: 0 },
  hooks: {
    beforeRequest: [
      (request) => {
        const token = getAccessToken();
        if (token && !request.headers.has('authorization')) {
          request.headers.set('authorization', `Bearer ${token}`);
        }
      },
    ],
    beforeRetry: [],
    afterResponse: [
      async (request, _options, response) => {
        if (response.status !== 401) return response;
        if (request.url.endsWith(REFRESH_PATH)) return response;
        if (request.headers.get('x-refresh-retry') === 'true') return response;

        const refreshed = await refreshAccessToken();
        if (!refreshed) return response;

        request.headers.set('authorization', `Bearer ${refreshed}`);
        request.headers.set('x-refresh-retry', 'true');
        return ky(request);
      },
    ],
    beforeError: [
      async (httpError) => {
        const apiError = await parseHttpError(httpError);
        // ky locks `message` after construction in some versions, so
        // defineProperty (not Object.assign / mutation) is the only
        // assignment that actually sticks. We also expose the typed
        // `apiError` on the thrown error for call sites that want
        // structured access (status code, details array, requestId).
        try {
          Object.defineProperty(httpError, 'message', {
            value: apiError.message,
            writable: true,
            configurable: true,
          });
        } catch {
          /* swallow — error is already informative even if message stays */
        }
        (httpError as unknown as { apiError: typeof apiError }).apiError = apiError;
        return httpError;
      },
    ],
  },
});

export { ApiError, NetworkError, getErrorMessage } from './errors';
